import { Component } from '@theme/component';

const STORAGE_KEY = 'auranod-email-popup-dismissed';
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Email popup: opens once after a delay or when the mouse leaves through the top of the window
 * (desktop exit intent), stays closed for a
 * week after dismissal, and reopens right away after a sign-up (success or error) so the visitor
 * sees the result.
 *
 * @typedef {{ dialog: HTMLDialogElement, code?: HTMLElement, copyButton?: HTMLButtonElement }} Refs
 * @extends {Component<Refs>}
 */
class EmailPopup extends Component {
  requiredRefs = ['dialog'];

  /** @type {number | undefined} */
  #timer;

  /** @type {AbortController | undefined} */
  #abortController;

  /** Auto-open listeners (timer + exit intent); dropped after the first open. */
  /** @type {AbortController | undefined} */
  #autoOpenController;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.refs.dialog.addEventListener('close', () => this.#snooze(), { signal });

    if (this.dataset.state) {
      this.#open();
      return;
    }
    if (window.Shopify?.designMode || this.#isSnoozed()) return;

    this.#autoOpenController = new AbortController();
    this.#timer = window.setTimeout(() => this.#open(), Number(this.dataset.delay || 30) * 1000);
    document.documentElement.addEventListener(
      'mouseleave',
      (event) => {
        if (event.clientY <= 0) this.#open();
      },
      { signal: this.#autoOpenController.signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.#timer);
    this.#abortController?.abort();
    this.#autoOpenController?.abort();
  }

  close() {
    this.refs.dialog.close();
  }

  /**
   * Closes the dialog when the click lands on the backdrop (the dialog element itself).
   * @param {MouseEvent} event
   */
  handleBackdropClick(event) {
    if (event.target === this.refs.dialog) this.close();
  }

  async copyCode() {
    const { code, copyButton } = this.refs;
    if (!code || !copyButton) return;
    try {
      await navigator.clipboard.writeText(code.textContent?.trim() ?? '');
      copyButton.textContent = copyButton.dataset.copiedLabel || copyButton.textContent;
    } catch {
      // Clipboard can be blocked; the code stays visible for manual copying.
    }
  }

  #open() {
    window.clearTimeout(this.#timer);
    this.#autoOpenController?.abort();
    const { dialog } = this.refs;
    if (dialog.open || document.querySelector('dialog[open]')) return;
    dialog.showModal();
  }

  #isSnoozed() {
    try {
      return Date.now() - Number(localStorage.getItem(STORAGE_KEY) || 0) < SNOOZE_MS;
    } catch {
      return false;
    }
  }

  #snooze() {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // Storage unavailable (private mode): the popup may show again on the next page view.
    }
  }
}

if (!customElements.get('email-popup')) {
  customElements.define('email-popup', EmailPopup);
}
